import { css } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import { UIColorPalette } from './ColorPalette';
import { graphql, useFragment } from './generated/gql';
import { UserSummaryFragment } from './generated/gql/graphql';
import { useGraphQLQuery, UserSummaryFragmentDoc } from './GraphQLHelpers';
import QueryStateRenderer from './QueryStateRenderer';

export const DevUserListQuery = graphql(`
  query DevUserList {
    developer {
      users {
        ...UserSummary
      }
    }
  }
`);

export default function UserListPage() {
  const state = useGraphQLQuery(DevUserListQuery);

  return (
    <div css={Styles.page}>
      <h1 css={Styles.title}>Users</h1>
      <QueryStateRenderer state={state}>
        {(data) => {
          const users = data.developer?.users ?? [];
          if (users.length === 0) {
            return <p css={Styles.emptyText}>No users found</p>;
          }
          return (
            <>
              <p css={Styles.countText}>{users.length} users</p>
              <table css={Styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Account Type</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const userSummary = useFragment(
                      UserSummaryFragmentDoc,
                      user,
                    );
                    return (
                      <UserEntry key={userSummary.id} user={userSummary} />
                    );
                  })}
                </tbody>
              </table>
            </>
          );
        }}
      </QueryStateRenderer>
    </div>
  );
}

function UserEntry({ user }: { user: UserSummaryFragment }) {
  const navigate = useNavigate();
  const joinDate = new Date(user.createdAt);

  return (
    <tr
      css={Styles.row}
      onClick={() => {
        navigate(`/user/${user.id}`);
      }}
    >
      <td css={Styles.cell}>{user.name}</td>
      <td css={Styles.cell}>{user.subtitle || '—'}</td>
      <td css={Styles.cell}>{joinDate.toLocaleDateString()}</td>
    </tr>
  );
}

const Styles = {
  page: css({
    padding: 16,
    maxWidth: 800,
    margin: '0 auto',
  }),
  title: css({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textAlign: 'center',
  }),
  countText: css({
    textAlign: 'center',
    color: 'rgb(150, 150, 150)',
    marginBottom: 16,
  }),
  emptyText: css({
    color: 'rgb(150, 150, 150)',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 32,
  }),
  table: css({
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: UIColorPalette.listBackground,
    borderRadius: 8,
    overflow: 'hidden',
    '& th, & td': {
      padding: 12,
      textAlign: 'left',
      borderBottom: `1px solid ${UIColorPalette.listItemBottomBorder}`,
    },
    '& th': {
      fontWeight: 'bold',
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
    '& tr:last-of-type td': {
      borderBottom: 'none',
    },
  }),
  row: css({
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  }),
  cell: css({}),
};
